import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Hero from './components/Hero';
import PreservativeFree from './components/PreservativeFree';
import About from './components/About';
import Counter from './components/Counter';
import WhereToBuy from './components/WhereToBuy';
import WholesaleForm from './components/WholesaleForm';
import Footer from './components/Footer';
import NotFound from './components/NotFound';
import { config } from './data/content';

// Home Page Component
function HomePage({ language, onLanguageChange, isScrolled }) {
  const sectionsRef = useRef([]);

  // Intersection Observer for fade-in animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    const currentSections = sectionsRef.current;
    currentSections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      currentSections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  return (
    <>
      <Header language={language} onLanguageChange={onLanguageChange} isScrolled={isScrolled} />
      <div ref={(el) => (sectionsRef.current[0] = el)} style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
        <Hero language={language} />
      </div>
      <div ref={(el) => (sectionsRef.current[1] = el)} style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
        <PreservativeFree language={language} />
      </div>
      <div ref={(el) => (sectionsRef.current[2] = el)} style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
        <About language={language} />
      </div>
      <div ref={(el) => (sectionsRef.current[3] = el)} style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
        <Counter language={language} />
      </div>
      <div ref={(el) => (sectionsRef.current[4] = el)} style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
        <WhereToBuy language={language} />
      </div>
      <div ref={(el) => (sectionsRef.current[5] = el)} style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
        <WholesaleForm language={language} />
      </div>
      <Footer language={language} />
    </>
  );
}

function App() {
  // Load saved language from localStorage or use default
  const getSavedLanguage = () => {
    try {
      const savedLanguage = localStorage.getItem('tuna-gwaizi-language');
      if (savedLanguage === 'ar' || savedLanguage === 'en') {
        return savedLanguage;
      }
    } catch (error) {
      console.error('Error loading language from localStorage:', error);
    }
    return config.defaultLanguage || 'ar';
  };

  const [language, setLanguage] = useState(getSavedLanguage);
  const [isScrolled, setIsScrolled] = useState(false);

  // Save language to localStorage and update document
  const handleLanguageChange = (newLanguage) => {
    try {
      localStorage.setItem('tuna-gwaizi-language', newLanguage);
    } catch (error) {
      console.error('Error saving language to localStorage:', error);
    }
    setLanguage(newLanguage);
  };

  useEffect(() => {
    // Set document direction based on language
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;

    // Handle scroll for header background
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [language]);

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route 
            path="/" 
            element={
              <HomePage 
                language={language} 
                onLanguageChange={handleLanguageChange} 
                isScrolled={isScrolled} 
              />
            } 
          />
          <Route path="*" element={<NotFound language={language} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;